import React, { useMemo } from "react";
import { Collapse, Checkbox, DatePicker, Button, Typography } from "antd";
import { PlusOutlined, MinusOutlined } from "@ant-design/icons";
import { useAppDispatch, useAppSelector } from "../../hooks/reduxHook";
import { setFilters } from "../../reducers/catalogFilterSlice";
import { CatalogFilterState } from "../../types";
import dayjs from "dayjs";
import "./index.css";
import { useListDevicesQuery } from "../../actions/devicesInQuery";
import { useListTrapSitesQuery } from "../../actions/trapsiteInQuery";
import { SpeciesList } from "../../utils/const";
import { toggleSpeciesFilter } from "../../utils/helpers";
import ChevronDownIcon from "@heroicons/react/24/outline/ChevronDownIcon";
import ChevronUpIcon from "@heroicons/react/24/outline/ChevronUpIcon";

const { Panel } = Collapse;
const { Text } = Typography;

const imageTypeOptions = [
  { label: <Text>Mosquito images</Text>, value: "mosquito" },
  { label: <Text>Mesh images</Text>, value: "mesh" },
  { label: <Text>Empty mesh images</Text>, value: "emptyMesh" },
  { label: <Text>Bycatch images</Text>, value: "bycatch" },
  { label: <Text>Debris images</Text>, value: "debris" },
  { label: <Text>Bookmarked images</Text>, value: "bookmarked" },
];

/** Returns [] (meaning "all") if the new selection equals the full set, otherwise returns newSelection. */
function resolveSelection(
  allValues: string[],
  newSelection: string[],
): string[] {
  const allSet = new Set(allValues);
  const newSet = new Set(newSelection);
  const allSelected =
    allSet.size === newSet.size && [...allSet].every((v) => newSet.has(v));
  return allSelected ? [] : newSelection;
}

const CatalogFilters: React.FC = () => {
  const dispatch = useAppDispatch();
  const filters = useAppSelector((state) => state.catalogFilter);

  const { data: devices } = useListDevicesQuery();
  const { data: trapSites } = useListTrapSitesQuery();

  const deviceOptions = useMemo(
    () =>
      devices?.items?.map((device) => ({
        label: device?.display_name,
        value: device?.uuid ?? "",
      })) ?? [],
    [devices],
  );
  const deviceAllValues = useMemo(
    () => deviceOptions.map((d) => d.value).filter(Boolean),
    [deviceOptions],
  );

  const trapSitesOptions = useMemo(() => {
    const options =
      trapSites?.items?.map((trapSite) => ({
        label: trapSite?.display_name,
        value: trapSite?._id ?? "",
      })) ?? [];
    options.push({ label: "Unassigned Location", value: "unassigned" });
    return options;
  }, [trapSites]);
  const trapSiteAllValues = useMemo(
    () => trapSitesOptions.map((t) => t.value).filter(Boolean),
    [trapSitesOptions],
  );

  const handleSpeciesChange = (genus: string, speciesName?: string) => {
    const genusEntry = SpeciesList.find((g) => g.genus === genus);
    const allSpeciesForGenus = genusEntry?.species ?? [];
    const newSpecies = toggleSpeciesFilter(
      filters.species,
      genus,
      speciesName,
      allSpeciesForGenus,
    );
    dispatch(setFilters({ species: newSpecies }));
  };

  const handleImageTypeChange = (checkedValues: string[]) => {
    const newImageTypes = Object.fromEntries(
      imageTypeOptions.map(({ value }) => [
        value,
        checkedValues.includes(value),
      ]),
    ) as CatalogFilterState["imageTypes"];
    dispatch(setFilters({ imageTypes: newImageTypes }));
  };

  return (
    <div className="catalog-filters-container">
      <div className="catalog-filters-title">
        <span>Filters</span>
      </div>

      <Collapse
        ghost
        expandIconPosition="end"
        expandIcon={({ isActive }) =>
          isActive ? (
            <ChevronUpIcon width={24} height={24} />
          ) : (
            <ChevronDownIcon width={24} height={24} />
          )
        }
      >
        <Panel
          header={
            <div className="flex flex-col">
              <div>Date range</div>
              <Button className="show-full-date-btn active-no-show-btn">
                Show full date range
              </Button>
            </div>
          }
          key="date"
        >
          <div className="date-range-inputs">
            <DatePicker
              placeholder="Start Date"
              value={
                filters.capturedAfter ? dayjs(filters.capturedAfter) : null
              }
              onChange={(date) =>
                dispatch(
                  setFilters({
                    capturedAfter: date ? date.toISOString() : null,
                  }),
                )
              }
            />
            <DatePicker
              placeholder="End Date"
              value={
                filters.capturedBefore ? dayjs(filters.capturedBefore) : null
              }
              onChange={(date) =>
                dispatch(
                  setFilters({
                    capturedBefore: date ? date.toISOString() : null,
                  }),
                )
              }
            />
          </div>
        </Panel>

        <Panel header="Species" key="species">
          {SpeciesList.map(({ genus, species }) => (
            <div key={genus} className="species-genus-item">
              <Collapse
                ghost
                expandIconPosition="end"
                expandIcon={({ isActive }) =>
                  isActive ? <MinusOutlined /> : <PlusOutlined />
                }
                className="species-genus-item-collapse"
              >
                <Panel
                  header={
                    <Checkbox
                      checked={filters.species[genus]?.selected}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleSpeciesChange(genus);
                      }}
                    >
                      <Text>{genus}</Text>
                    </Checkbox>
                  }
                  key={genus}
                >
                  <div className="species-list">
                    {species.map((s) => (
                      <div key={s}>
                        <Checkbox
                          checked={filters.species[genus]?.species[s]}
                          onChange={() => handleSpeciesChange(genus, s)}
                        >
                          <Text>{`${genus} ${s}`}</Text>
                        </Checkbox>
                      </div>
                    ))}
                  </div>
                </Panel>
              </Collapse>
            </div>
          ))}
          {["Undefined", "Unknown"].map((label) => (
            <div key={label} className="species-genus-item-special">
              <Checkbox
                checked={filters.species[label]?.selected}
                onChange={() => handleSpeciesChange(label)}
              >
                <Text>{label}</Text>
              </Checkbox>
            </div>
          ))}
        </Panel>

        <Panel header="Image types" key="imageTypes">
          <Checkbox.Group
            options={imageTypeOptions}
            value={Object.entries(filters.imageTypes)
              .filter(([_, checked]) => checked)
              .map(([key]) => key)}
            onChange={handleImageTypeChange}
            className="check-box-image-types scrollable-list"
          />
        </Panel>

        <Panel header="Locations" key="locations">
          <div className="scrollable-list">
            {trapSitesOptions.map((trapSite) => (
              <div key={trapSite.value}>
                <Checkbox
                  checked={
                    filters.trapsites.length === 0 ||
                    filters.trapsites.includes(trapSite.value)
                  }
                  onChange={(e) => {
                    const current =
                      filters.trapsites.length === 0
                        ? trapSiteAllValues
                        : filters.trapsites;
                    const newLocs = e.target.checked
                      ? [...current, trapSite.value]
                      : current.filter((l) => l !== trapSite.value);
                    dispatch(
                      setFilters({
                        trapsites: resolveSelection(trapSiteAllValues, newLocs),
                      }),
                    );
                  }}
                >
                  <Text>{trapSite.label}</Text>
                </Checkbox>
              </div>
            ))}
          </div>
        </Panel>

        <Panel header="Scout Devices" key="devices">
          <div className="scrollable-list">
            {deviceOptions.map((device) => (
              <div key={device.value}>
                <Checkbox
                  checked={
                    filters.devices.length === 0 ||
                    filters.devices.includes(device.value)
                  }
                  onChange={(e) => {
                    const current =
                      filters.devices.length === 0
                        ? deviceAllValues
                        : filters.devices;
                    const newDevs = e.target.checked
                      ? [...current, device.value]
                      : current.filter((d) => d !== device.value);
                    dispatch(
                      setFilters({
                        devices: resolveSelection(deviceAllValues, newDevs),
                      }),
                    );
                  }}
                >
                  <Text>{device.label}</Text>
                </Checkbox>
              </div>
            ))}
          </div>
        </Panel>
      </Collapse>
    </div>
  );
};

export default CatalogFilters;
